import { useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { useGetPrimaryFieldsQuery } from "~/graphql/supergraphHooks";
import type { GetPrimaryFieldsQuery } from "~/graphql/supergraphTypes";

export type PrimaryFieldCatalogueItem =
  GetPrimaryFieldsQuery["finpubPrimaryFields"]["items"][number];

/** The Semantic Layer caps a page at 1000 rows (`PAGINATION_LIMIT_MAX` in
 * finpub-data-service). Omitting `limit` falls back to a 300-row server
 * default that silently truncates larger catalogues. */
const PRIMARY_FIELDS_PAGE_LIMIT = 1000;

type PageError = {
  /** The arena the failed page belonged to. A page failure only blocks
   * pagination for its own arena — switching arenas starts fresh. */
  arenaId: number | null;
  error: ApolloError;
};

/** Fetch the complete primary-field catalogue.
 *
 * The catalogue backs per-column metadata lookups — rule attribute pickers,
 * clause labels, type inference, and level support — which are all wrong in
 * subtle ways when a column is missing. Unlike the Add Field Picker (which
 * pages on demand), metadata consumers need every column, so this walks the
 * listing page by page until `items` covers `pagination.total`. The
 * `finpubPrimaryFields` field policy merges each page into one cache entry
 * by offset, which keeps concurrent hook instances from duplicating items
 * and lets a fully cached catalogue survive remounts while page one
 * refreshes in the background.
 *
 * Returns `undefined` while pages are still loading, so consumers see either
 * no catalogue (their degraded/not-loaded handling applies) or a complete
 * one — never a partial listing masquerading as complete. A failed page
 * fetch stops pagination and returns the pages already loaded. */
export const useAllPrimaryFields = (
  arenaId: number | null,
): ReadonlyArray<PrimaryFieldCatalogueItem> | undefined => {
  const {
    data,
    error: queryError,
    fetchMore,
  } = useGetPrimaryFieldsQuery({
    variables: { arenaId, limit: PRIMARY_FIELDS_PAGE_LIMIT, offset: 0 },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [pageError, setPageError] = useState<PageError>();
  const activePageError = pageError?.arenaId === arenaId ? pageError.error : undefined;

  const listing = data?.finpubPrimaryFields;
  const hasListing = listing != null;
  const loadedCount = listing?.items.length ?? 0;
  // Completeness is items-vs-total rather than the last page's `hasMore`:
  // after a remount, `cache-and-network` re-requests page one, whose
  // `hasMore: true` would otherwise flip a fully cached catalogue back to
  // "loading" and re-walk every page.
  const isComplete = hasListing && loadedCount >= (listing?.pagination.total ?? 0);
  const stopped = queryError != null || activePageError != null;

  // Walk the remaining pages; the field policy merges each into the cache.
  // Depending only on primitives means a response that fails to grow the
  // listing cannot re-trigger a fetch for the same offset.
  useEffect(() => {
    if (!hasListing || isComplete || stopped) return;
    let active = true;
    void fetchMore({ variables: { offset: loadedCount } }).catch((err: unknown) => {
      if (active) {
        setPageError({
          arenaId,
          error:
            err instanceof ApolloError ? err : new ApolloError({ errorMessage: String(err) }),
        });
      }
    });
    return () => {
      active = false;
    };
  }, [hasListing, isComplete, stopped, loadedCount, fetchMore, arenaId]);

  return isComplete || stopped ? listing?.items : undefined;
};
